const ACTIONS = ['create', 'update'];

/** Keep alternative forms separate; fields inside one form are required together. */
function fromSchema(schema, action, readOnly) {
    const hasInput = Object.values(schema.properties || {}).some(property => property['x-editable'] === true);
    if (readOnly || !hasInput || (action && !ACTIONS.includes(action))) return undefined;
    // The complete form has already resolved optional wrappers and duplicate inputs.
    // Re-reading individual x-required hints here would undo that resolution.
    const required = (schema.required || []).filter(name => !name.includes(':') && schema.properties[name]?.['x-editable'] === true).sort();
    const properties = formArrayConstraints(schema.properties || {});
    const form = { required, properties };
    return Object.fromEntries((action ? [action] : ACTIONS).map(writeAction => [writeAction, [form]]));
}

function merge(existing, incoming) {
    if (!existing && !incoming) return undefined;
    return Object.fromEntries(
        ACTIONS.filter(action => existing?.[action] || incoming?.[action]).map(action => {
            const alternatives = [...(existing?.[action] || []), ...(incoming?.[action] || [])];
            const unique = new Map(
                alternatives.map(form => {
                    const normalized = { ...form, required: [...new Set(form.required)].sort() };
                    return [JSON.stringify(normalized), normalized];
                })
            );
            return [
                action,
                [...unique.keys()]
                    .sort()
                    .map(key => unique.get(key))
                    .sort((first, second) => first.required.length - second.required.length)
            ];
        })
    );
}

function common(alternatives) {
    if (!alternatives?.length) return [];
    return alternatives[0].required.filter(field => alternatives.every(form => form.required.includes(field))).sort();
}

function universal(requirements) {
    if (!ACTIONS.every(action => requirements[action]?.length)) return [];
    return common(ACTIONS.flatMap(action => requirements[action]));
}

/** Shared values must allow every authoritative write form; an operation can narrow them again. */
function sharedArrayConstraints(properties, requirements) {
    const forms = ACTIONS.flatMap(action => requirements[action] || []);
    return sharedPropertyConstraints(properties, forms);
}

function formArrayConstraints(properties) {
    return Object.fromEntries(
        Object.keys(properties)
            .sort()
            .flatMap(name => {
                const property = properties[name];
                if (property['x-editable'] === false) return [];
                const children = formArrayConstraints(property.properties || {});
                const itemChildren = formArrayConstraints(property.items?.properties || {});
                const constraints = {
                    ...(property.type === 'array' && property['x-editable'] === true
                        ? {
                              minItems: property.minItems ?? 0,
                              ...(property.items?.type === 'string'
                                  ? { items: stringItemConstraints(property.items) }
                                  : {})
                          }
                        : {}),
                    ...(Object.keys(children).length ? { properties: children } : {}),
                    ...(Object.keys(itemChildren).length ? { items: { properties: itemChildren } } : {})
                };
                return Object.keys(constraints).length ? [[name, constraints]] : [];
            })
    );
}

function sharedPropertyConstraints(properties, forms) {
    return Object.fromEntries(
        Object.entries(properties).map(([name, property]) => {
            const bounds = forms.map(form => form.properties?.[name]).filter(Boolean);
            if (!bounds.length) return [name, property];
            const result = { ...property };
            const arrayBounds = bounds.filter(bound => bound.minItems !== undefined);
            if (property.type === 'array' && arrayBounds.length) {
                const minItems = Math.min(...arrayBounds.map(bound => bound.minItems));
                if (minItems) result.minItems = minItems;
                else delete result.minItems;
            }
            if (property.properties) result.properties = sharedPropertyConstraints(property.properties, bounds);
            const items = bounds.filter(bound => bound.items).map(bound => bound.items);
            if (property.items?.type === 'string' && items.length) {
                const alternatives = [...new Set(items.map(item => JSON.stringify(item)))]
                    .sort()
                    .map(item => JSON.parse(item));
                const constraints = alternatives.length === 1 ? alternatives[0] : { anyOf: alternatives };
                result.items = withStringItemConstraints(property.items, constraints);
            } else if (property.items?.properties && items.length) {
                result.items = {
                    ...property.items,
                    properties: sharedPropertyConstraints(property.items.properties, items)
                };
            }
            return [name, result];
        })
    );
}

/** These are the string constraints emitted by MultiPickerShortText. */
function stringItemConstraints(schema) {
    return {
        minLength: schema.minLength ?? 0,
        ...(schema.maxLength !== undefined ? { maxLength: schema.maxLength } : {}),
        ...(schema.pattern !== undefined ? { pattern: schema.pattern } : {}),
        ...(schema.anyOf ? { anyOf: schema.anyOf } : {})
    };
}

function withStringItemConstraints(schema, constraints) {
    const result = { ...schema };
    for (const key of ['minLength', 'maxLength', 'pattern', 'anyOf']) delete result[key];
    return { ...result, ...constraints };
}

module.exports = {
    fromSchema,
    merge,
    common,
    universal,
    sharedArrayConstraints,
    stringItemConstraints,
    withStringItemConstraints
};
